import { Link } from "@tanstack/react-router";
import { Star } from "lucide-react";
import type { ReactNode } from "react";
import type { SidebarNavItem } from "./sidebarNavData";

interface SidebarNavRowProps {
  item: SidebarNavItem;
  isActive: boolean;
  isCollapsed: boolean;
  theme: "dark" | "light";
  onNavigate: () => void;
}

export function SidebarNavRow({
  item,
  isActive,
  isCollapsed,
  theme,
  onNavigate,
}: SidebarNavRowProps) {
  const containerClass = `flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 transition-all duration-200 relative group ${
    isActive
      ? theme === "dark"
        ? "bg-primary/15 text-primary"
        : "bg-primary text-primary-foreground"
      : theme === "dark"
        ? "text-muted-foreground hover:text-white hover:bg-white/5"
        : "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
  } ${item.isArchived ? "opacity-70" : ""} ${isCollapsed ? "justify-center" : ""}`;

  const ariaLabel = item.isArchived
    ? `${item.label} — coming in v1.x`
    : item.label;

  const inner: ReactNode = (
    <>
      {isActive && theme === "dark" && !isCollapsed && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-3/4 rounded-r-full bg-primary shadow-[var(--glow-epic-subtle)]" />
      )}
      <div className="relative flex items-center gap-3 shrink-0">
        <item.icon size={18} />
        {item.isMvp && (
          <Star
            size={10}
            className="absolute -top-1 -right-1 text-[var(--p-legendary)]"
            fill="var(--p-legendary)"
            aria-hidden
          />
        )}
      </div>
      {!isCollapsed && (
        <span className="text-sm flex-1 min-w-0 truncate">{item.label}</span>
      )}
      {!isCollapsed && item.isArchived && (
        <span
          className={`shrink-0 exp-caption uppercase tracking-wider px-1.5 py-0.5 rounded-md ${
            theme === "dark"
              ? "bg-white/5 text-muted-foreground"
              : "bg-muted text-muted-foreground"
          }`}
          aria-hidden
        >
          v1.x
        </span>
      )}
      {!isCollapsed && !item.isArchived && item.shortcutHint && (
        <kbd
          className={`hidden sm:inline-flex shrink-0 exp-caption px-1.5 py-0.5 rounded-md border font-sans tracking-tight ${
            theme === "dark"
              ? "border-white/15 bg-white/5 text-muted-foreground"
              : "border-border bg-muted text-muted-foreground"
          }`}
        >
          {item.shortcutHint}
        </kbd>
      )}
      {isCollapsed && (
        <div
          className={`absolute left-full ml-2 px-3 py-2 rounded-lg whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-50 ${
            theme === "dark" ? "glass-thick text-white" : "bg-muted text-white"
          }`}
        >
          <div className="text-sm">{item.label}</div>
          {item.isArchived && (
            <div className="exp-caption uppercase tracking-wider opacity-70 mt-0.5">
              Coming v1.x
            </div>
          )}
        </div>
      )}
    </>
  );

  if (item.isArchived) {
    return (
      <a
        href={item.to}
        onClick={onNavigate}
        className={containerClass}
        aria-label={ariaLabel}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={containerClass}
      aria-label={ariaLabel}
    >
      {inner}
    </Link>
  );
}
